// 브라우저 오류 수집기(D5b, docs/COMMUNITY-SUPPORT-PRD.md §5): window 'error'·'unhandledrejection'·WebGL 컨텍스트 손실 → /api/telemetry/error.
// 원칙: 게임 루프 밖(이벤트 + 미뤄 보내기), 같은 서명은 페이지당 1번·전체 20번, 429면 이 페이지에서 멈춤, 개인정보 없음(계정·입력 내용 안 보냄).
// 최근 오류 5개는 메모리에만 두고 버그 신고 진단(src/community/diagnostics.ts)에 쓴다.
import { APP_BUILD, APP_VERSION } from './build';
import { cleanMessage, errorSignature, firstFrame, parseErrorReport, type ErrorKind, type ErrorReport } from './signature';

export const MAX_REPORTS_PER_PAGE = 20;
export const RECENT_KEEP = 5;
export const TELEMETRY_URL = '/api/telemetry/error';

export interface RecentError { sig: string; kind: ErrorKind; message: string; at: number }

type Listener = (type: string, fn: (event: any) => void) => void;
export interface CollectorEnv {
  win: { addEventListener: Listener; innerWidth: number; innerHeight: number };
  doc?: { getElementById(id: string): { addEventListener: Listener } | null } | null;
  ua: string;
  fetch: (url: string, init: RequestInit) => Promise<{ status: number }>;
  /** 보내기를 미루는 함수(기본 setTimeout 0) */
  defer?: (fn: () => void) => void;
  now?: () => number;
}

/** UA → 브라우저 계열(서버 검증 [\w.:/x -]{0,64}) */
export function browserFamily(ua: string): string {
  if (/KAKAOTALK|Instagram|NAVER|Line\/|FBAN|FBAV|DaumApps/i.test(ua)) return 'inapp';
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/Edg\//i.test(ua)) return 'edge';
  if (/Firefox|FxiOS/i.test(ua)) return 'firefox';
  if (/CriOS|Chrome\//i.test(ua)) return 'chrome';
  if (/Safari\//i.test(ua)) return 'safari';
  return 'other';
}
export function osFamily(ua: string): string {
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'mac';
  if (/Linux|CrOS/i.test(ua)) return 'linux';
  return 'other';
}

export interface ErrorCollector {
  /** 최근 오류(새것 먼저, 최대 5개) */
  recent(): RecentError[];
  /** 직접 보고(게임 코드가 잡은 치명 오류 등) */
  report(kind: ErrorKind, message: unknown, stack?: unknown): void;
}

export function createErrorCollector(env: CollectorEnv, scene: () => string): ErrorCollector {
  const seen = new Set<string>();
  const recent: RecentError[] = [];
  let sent = 0, stopped = false;
  const now = env.now ?? (() => Date.now());
  const defer = env.defer ?? ((fn: () => void) => { setTimeout(fn, 0); });
  const browser = browserFamily(env.ua), os = osFamily(env.ua);

  function report(kind: ErrorKind, message: unknown, stack?: unknown): void {
    try {
      const msg = cleanMessage(message) || '(no message)';
      const frame = firstFrame(stack);
      const sig = errorSignature(kind, msg, frame);
      recent.unshift({ sig, kind, message: msg.slice(0, 80), at: now() });
      if (recent.length > RECENT_KEEP) recent.length = RECENT_KEEP;
      if (stopped || seen.has(sig) || sent >= MAX_REPORTS_PER_PAGE) return;
      seen.add(sig);
      sent++;
      let sceneName = 'unknown';
      try { sceneName = scene(); } catch { /* 장면을 못 읽으면 unknown */ }
      const body: ErrorReport | null = parseErrorReport({
        kind, message: msg, frame, version: APP_VERSION, build: APP_BUILD, scene: sceneName,
        browser, os, screen: Math.round(env.win.innerWidth) + 'x' + Math.round(env.win.innerHeight),
      });
      if (!body) return;
      defer(() => {
        env.fetch(TELEMETRY_URL, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), credentials: 'same-origin', keepalive: true })
          .then((res) => { if (res.status === 429 || res.status === 403) stopped = true; }, () => { /* 오프라인: 조용히 */ });
      });
    } catch { /* 수집기 자체 오류는 삼킨다(오류 처리기 안의 오류가 다시 오류를 부르지 않게) */ }
  }

  env.win.addEventListener('error', (event: { message?: string; error?: { message?: string; stack?: string }; filename?: string }) => {
    // 이미지·스크립트 로드 실패(ErrorEvent 아님)는 message가 없다 → 무시
    if (!event || (typeof event.message !== 'string' && !event.error)) return;
    report('error', event.error?.message ?? event.message, event.error?.stack);
  });
  env.win.addEventListener('unhandledrejection', (event: { reason?: unknown }) => {
    const r = event?.reason as { message?: string; stack?: string } | string | undefined;
    if (r && typeof r === 'object') report('rejection', r.message ?? String(r), r.stack);
    else report('rejection', String(r));
  });
  const canvas = env.doc?.getElementById('game');
  canvas?.addEventListener('webglcontextlost', () => report('webgl-lost', 'WebGL context lost'));

  return { recent: () => recent.slice(), report };
}

let active: ErrorCollector | null = null;

/** 부팅 맨 처음 한 번(게임이 만들어지기 전 오류도 잡게). 실패해도 게임은 그대로 */
export function startErrorCollector(scene: () => string): ErrorCollector | null {
  if (active) return active;
  try {
    active = createErrorCollector({
      win: window, doc: document, ua: navigator.userAgent,
      fetch: (url, init) => fetch(url, init),
    }, scene);
  } catch { active = null; }
  return active;
}

/** 게임 코드가 직접 보고(수집기가 없으면 아무 일도 안 한다) */
export function reportTelemetry(kind: ErrorKind, message: unknown): void {
  active?.report(kind, message);
}

/** 최근 오류(버그 신고 진단용). 수집기가 없으면 빈 배열 */
export function recentErrors(): RecentError[] {
  return active ? active.recent() : [];
}
